import { clientColumns } from "@/columns";
import {
  AddClientModal,
  DeleteConfirmModal,
  DynamicTable,
  FilterPopover,
  IconImage,
  PageHeader,
  RestrictUserModal,
  UnblockUserModal,
  UpdateClientModal,
} from "@/components";
import {
  black_filter_img,
  // clientInitialPermissions,
  search_img,
} from "@/constants";
import { useDebounce, useSearchQuery, useSnackbar } from "@/hooks";
import {
  useDeleteClientMutation,
  useGetAllClientsQuery,
  useGetAllSpOwnerPartnersQuery,
  useUpdateClientStatusMutation,
} from "@/services";
import { Box, InputAdornment, Typography } from "@mui/material";
import { FC, MouseEvent, ReactElement, useEffect, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { useLocation } from "react-router-dom";
import {
  HeaderRightNode,
  StyledContainedButton,
  StyledTextInput,
} from "./styles";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";

const initialModalState = {
  type: "", // "delete", "restrict", "unblock"
  open: false,
  clientName: "",
  _id: "",
  isRestricted: false,
  email: "",
};

const initalFilterState: BasicFilters = {
  status: "",
  dateAdded: "",
  partner: "",
};

const defaultClientDetails: Client = {
  _id: "",
  firstName: "",
  email: "",
  createdAt: new Date(),
  isRestricted: false,
  profileImage: "",
};

const SPOWNERClients: FC = (): ReactElement => {
  const { updateQuery, getQueryValue } = useSearchQuery();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null
  );
  const [filterState, setFilterState] = useState(initalFilterState);
  const [modalState, setModalState] = useState(initialModalState);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showUpdateClientModal, setShowUpdateClientModal] = useState(false);
  const [selectedPartner, setSelectedPartner] =
    useState<SPOwnerPartnerTypes | null>(null);

  const [clientDetails, setClientDetails] =
    useState<Client>(defaultClientDetails);
  const [shouldFetchPartners, setShouldFetchPartners] = useState(false);
  const [updateClientStatus, { isLoading: updateClientStatusLoading }] =
    useUpdateClientStatusMutation();
  const filterCount = Object.values(filterState).filter(Boolean).length;
  const location = useLocation();
  const hasPartner = new URLSearchParams(location.search).get("partner");

  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const user = useAppSelector((state) => state.auth.user);
  const isTDAdminSP =
    user?.userRole === roles.TDADMIN &&
    selectedTenant?.tenantType === "SERVICE_PROVIDER";

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllClientsQuery(
    {
      pageSize: limit,
      pageNo: page,
      searchKeyword: getQueryValue("search"),
      ...(filterState.status && {
        isRestricted: filterState.status !== "active" ? true : false,
      }),
      ...(isTDAdminSP && {
        tenantId: selectedTenant?._id,
      }),
      startDate: filterState.dateAdded,
      assignedPartner: filterState.partner || hasPartner || "",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: partnerData } = useGetAllSpOwnerPartnersQuery(
    {
      searchKeyword: "",
      ...(isTDAdminSP && {
        tenantId: selectedTenant?._id,
      }),
    },
    {
      skip: !shouldFetchPartners && !isTDAdminSP,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (hasPartner) {
      setShouldFetchPartners(true);
    }
  }, []);

  useEffect(() => {
    if (hasPartner) {
      const found = partnerData?.data?.find((ele) => ele?._id === hasPartner);
      const body = {
        label: found?.firstName + " " + found?.lastName,
        value: found?._id,
      };
      initalFilterState.partner = body.value || "";
      setFilterState((prev) => ({ ...prev, partner: body.value || "" }));
    } else {
      setFilterState((prev) => ({ ...prev, partner: "" }));

      initalFilterState.partner = "";
    }
  }, [partnerData, hasPartner]);

  const { data = [], meta } = apiData || {};
  const { show } = useSnackbar();
  const { countDocs: totalCount = 0 } = meta || {};
  const { open, clientName } = modalState;

  const [deleteClient, { isLoading: deleteClientLoading }] =
    useDeleteClientMutation();

  const debouncedSearch = useDebounce((val: string) => {
    updateQuery({ search: val });
  }, 500);

  const handleRowClick = (client: Client) => {
    setClientDetails(client);
    setShowUpdateClientModal(true);
  };

  const handleCloseModal = () => {
    setModalState(initialModalState);
  };

  const handleTriggerModal = (item: Client, type: string) => {
    setModalState({
      type,
      open: true,
      clientName: item.firstName,
      _id: item._id,
      isRestricted: item.isRestricted,
      email: item.email,
    });
  };

  const handleOpenFilterPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleOpenAddClientModal = () => {
    setShowAddClientModal(true);
    setClientDetails(defaultClientDetails);
    setSelectedPartner(null);
  };

  const handleFilterChange = async (e: BasicFilters) => {
    setFilterState(e);
  };

  const handleDeleteClient = async () => {
    const userId = {
      userId: modalState?._id as string,
      ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
    };
    try {
      await deleteClient(userId).unwrap();
      handleCloseModal();
    } catch (error) {
      if (error instanceof Error) {
        show(error.message, "", "error");
      }
    }
  };

  const handleUpdateClientStatus = async () => {
    try {
      await updateClientStatus({
        email: modalState.email,
        isRestricted: !modalState.isRestricted,
      }).unwrap();
      show(
        `Client ${modalState.isRestricted ? "unblocked" : "restricted"}!`,
        "",
        "success"
      );
      setModalState(initialModalState);
    } catch (error: any) {
      show(error.message, "", "error");
    }
  };

  return (
    <>
      <PageHeader
        title={`Clients`}
        count={totalCount}
        rightNode={
          <HeaderRightNode gap={2}>
            <StyledTextInput
              type="text"
              placeholder="Search"
              handleChangeValue={debouncedSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconImage src={search_img} size="auto" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ position: "relative" }}>
              {filterCount > 0 && (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "grid",
                    placeContent: "center",
                    backgroundColor: "error.main",
                    color: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "-10px",
                    right: "-9px",
                    zIndex: "1",
                  }}
                >
                  <Typography variant="caption" fontWeight={500} color="white">
                    {filterCount}
                  </Typography>
                </Box>
              )}

              <StyledContainedButton
                size="large"
                title="Filter"
                variant="outlined"
                style={{ borderColor: "#E6E6E9", height: "46px" }}
                startIcon={<IconImage src={black_filter_img} size="auto" />}
                onClick={handleOpenFilterPopover}
              />
            </Box>
            <StyledContainedButton
              startIcon={<IoMdAdd />}
              size="large"
              title="Add Client"
              onClick={handleOpenAddClientModal}
            />
          </HeaderRightNode>
        }
      />
      <DynamicTable
        data={data}
        columns={clientColumns({
          handleTriggerModal,
          isSuperAdmin: user?.userRole === roles.TDADMIN,
        })}
        isLoading={isLoading || isFetching}
        handlePageChange={setPage}
        handleLimitChange={setLimit}
        limit={limit}
        page={page}
        total={totalCount}
        hidePagination={false}
        onRowClick={handleRowClick}
        isFullHeight
        stickyHeader
        hideScrollBars
        sx={{
          flex: 1,
          "& .MuiTable-root": {
            borderCollapse: "collapse",
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
        }}
      />
      <FilterPopover
        anchorEl={anchorElement}
        onFiltersChange={handleFilterChange}
        initialFilterState={initalFilterState}
        onClose={() => setAnchorElement(null)}
        partnerData={partnerData}
      />
      {/* Delete confirmation Modal */}
      {modalState.type === "delete" && (
        <DeleteConfirmModal
          open={open}
          itemName={`Client “${clientName}”`}
          subText="client"
          onConfirm={handleDeleteClient}
          maxWidth="xs"
          onClose={handleCloseModal}
          btnLoader={deleteClientLoading}
        />
      )}
      {/* Restrict User Modal */}
      {modalState.type === "restrict" && (
        <RestrictUserModal
          description="client"
          open={open}
          name={clientName}
          onConfirm={handleUpdateClientStatus}
          onClose={handleCloseModal}
          loading={updateClientStatusLoading}
        />
      )}
      {/* Unblock User Modal */}
      {modalState.type === "unblock" && (
        <UnblockUserModal
          description="client"
          open={open}
          name={clientName}
          onConfirm={handleUpdateClientStatus}
          onClose={handleCloseModal}
          loading={updateClientStatusLoading}
        />
      )}
      {/* Add Client Modal */}
      {showAddClientModal && (
        <AddClientModal
          show={showAddClientModal}
          setShow={setShowAddClientModal}
          selectedPartner={selectedPartner}
          setSelectedPartner={setSelectedPartner}
        />
      )}
      {/* Update Client Modal */}
      {showUpdateClientModal && (
        <UpdateClientModal
          show={showUpdateClientModal}
          setShow={setShowUpdateClientModal}
          selectedPartner={selectedPartner}
          setSelectedPartner={setSelectedPartner}
          clientDetails={clientDetails}
          setClientDetails={setClientDetails}
        />
      )}
    </>
  );
};

export default SPOWNERClients;
